import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { chatService } from '../services/api';
import signalRService from '../services/signalRService';
import { scheduleIdle } from '../utils/scheduleIdle';

const ChatContext = createContext(null);

export const ChatProvider = ({ children }) => {
    const { user } = useAuth();
    const [unreadChatsCount, setUnreadChatsCount] = useState(0);
    const chatsRef = useRef([]);

    const computeUnread = (chats) =>
        chats.filter(c => (c.unreadCount || 0) > 0).length;

    const loadUnread = useCallback(async () => {
        if (!user) {
            setUnreadChatsCount(0);
            chatsRef.current = [];
            return;
        }
        try {
            const response = await chatService.getChats();
            chatsRef.current = response.data;
            setUnreadChatsCount(computeUnread(response.data));
        } catch {
            // silently ignore
        }
    }, [user]);

    useEffect(() => {
        return scheduleIdle(loadUnread, 1600);
    }, [loadUnread]);

    useEffect(() => {
        if (!user) return;

        const handleChatUpdated = (chatUpdate) => {
            chatsRef.current = chatsRef.current.map(chat => {
                if (chat.id !== chatUpdate.id) return chat;
                return {
                    ...chat,
                    lastMessageContent: chatUpdate.lastMessageContent,
                    lastMessageHasAttachments: chatUpdate.lastMessageHasAttachments,
                    lastMessageSentAt: chatUpdate.lastMessageSentAt,
                    unreadCount: (chat.unreadCount || 0) + 1,
                };
            });

            const exists = chatsRef.current.some(c => c.id === chatUpdate.id);
            if (!exists) {
                loadUnread();
                return;
            }

            setUnreadChatsCount(computeUnread(chatsRef.current));
        };

        const handleMessagesRead = (chatIdStr, userIdStr) => {
            if (userIdStr !== user?.id) return;
            chatsRef.current = chatsRef.current.map(chat =>
                chat.id === chatIdStr ? { ...chat, unreadCount: 0 } : chat
            );
            setUnreadChatsCount(computeUnread(chatsRef.current));
        };

        signalRService.on('ChatUpdated', handleChatUpdated);
        signalRService.on('MessagesRead', handleMessagesRead);

        return () => {
            signalRService.off('ChatUpdated', handleChatUpdated);
            signalRService.off('MessagesRead', handleMessagesRead);
        };
    }, [user, loadUnread]);

    const markChatAsRead = useCallback((chatId) => {
        chatsRef.current = chatsRef.current.map(chat =>
            chat.id === chatId ? { ...chat, unreadCount: 0 } : chat
        );
        setUnreadChatsCount(computeUnread(chatsRef.current));
    }, []);

    return (
        <ChatContext.Provider value={{ unreadChatsCount, markChatAsRead, refreshUnread: loadUnread }}>
            {children}
        </ChatContext.Provider>
    );
};

export const useChat = () => useContext(ChatContext);
